import React, { Fragment, useState, useCallback, useMemo } from "react";
import {
  Button,
  Col,
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Input,
  Row,
} from "reactstrap";
import { useDispatch, useSelector } from "react-redux";
import { format } from "date-fns";

import styles from "./tagstyles.module.scss";
import DropdownIcon from "../../assets/images/arrow-square-down.svg";
import SortIcon from "../../assets/images/sort-icon.svg";
import ArrowDownBlack from "../../assets/images/arrow-down-black.svg";
import TagsIcon from "../../assets/images/menu-icons/tag-icon.png";
import AssignTagsModal from "./TagsModals/AssignTagsModal";
import TagDetailModal from "./TagsModals/TagDetailModal";
import ImportTagsModal from "./TagsModals/ImportTagsModal";
import EnableDisable from "./TagsModals/EnableDisable";
import DataTableComponent from "../Shared/Table";
import Filterdropdown from "../LocationList/Filterdropdown";
import CuteMascotSpinner from "../../global/CuteMascotSpinner";
import {
  clearTagSearch,
  searchTags,
  sortTags,
} from "../Redux/Reducer/appReducer";

const TagsList = () => {
  const userData = useSelector((state) => state.authReducer);
  const [loading, setLoading] = useState(false);
  const [isOptionsDropdownOpen, setIsOptionsDropdownOpen] = useState(false);
  const [isAssignTagsModalOpen, setIsAssignTagsModalOpen] = useState(false);
  const [isTagDetailModalOpen, setIsTagDetailModalOpen] = useState(false);
  const [isImportTagsModalOpen, setIsImportTagsModalOpen] = useState(false);
  const [isEnableDisableModalOpen, setIsEnableDisableModalOpen] =
    useState(false);
  const [currentTag, setCurrentTag] = useState({});

  const dispatch = useDispatch();
  const appData = useSelector((state) => state.appReducer);
  const tagsSearchResults = useSelector(
    (state) => state.appReducer.tagsSearchResults
  );

  const isAdmin = userData?.role === "admin";

  const tagsData = !isAdmin
    ? tagsSearchResults.filter((tag) => tag.sellerId === userData.id)
    : tagsSearchResults || [];

  const sellerData = appData.sellers || [];
  const productsData = appData.products || [];
  const brandsData = appData.brands || [];
  const locationData = appData.locations || [];

  const noTags = !tagsData.length;

  const getItemById = (data, id, property) => {
    return (
      data?.find((item) => Number(item.id) === Number(id))?.[property] ||
      "Unknown"
    );
  };

  const getProductImage = (productId) => {
    const product = productsData.find((product) => product.id === productId);
    // 2026-06-03 null-safety: product.images can be null (no images uploaded yet)
    // or an empty array. Previously crashed the whole Tags table on first row.
    return product?.images?.[0] || "Unknown";
  };

  const getBrandNameById = (brandId) =>
    getItemById(brandsData, brandId, "brandName");
  const getProductById = (productId) =>
    getItemById(productsData, productId, "name");
  const getSellerById = (sellerId) => getItemById(sellerData, sellerId, "name");
  const getLocationById = (locationId) =>
    getItemById(locationData, locationId, "name");
  // const getLocationById = (locationId) => handleLocation(locationId);

  const tagsTableData = useMemo(() => {
    return tagsData.map((tag) => {
      const createdDate = format(
        new Date(tag.createdAt),
        "h:mm a M/d/yy"
      ).split(" ");
      createdDate.splice(2, 0, "on");
      const dateString = createdDate.join(" ");

      return {
        ...tag,
        action: "",
        seller: tag.sellerId || "",
        assignmentDate: tag.updatedAt
          ? format(new Date(tag.updatedAt), "M/d/yy h:mm a z")
          : "-",
        product: tag.productId || "",
        image: tag.image || "",
        brandName: tag.brandId || "",
        dateAdded: dateString || "",
        Uid: tag.id || "",
      };
    });
  }, [tagsData]);

  const tagsColumns = useMemo(
    () => [
      {
        name: "Brand Name",
        selector: (row) => {
          getBrandNameById(row.brandId);
        },
        sortable: false,
        center: false,
        wrap: true,
        minWidth: "15%",
        maxWidth: "15%",
        cell: (row) => (
          <div className={styles.brandname_container}>
            <img src={getProductImage(row.productId)} alt="" />
            <div style={{ minWidth: "66px", flexGrow: "1" }}>
              {getBrandNameById(row.brandId)}
            </div>
          </div>
        ),
      },
      {
        name: "Seller",
        selector: (row) => getSellerById(row.sellerId),
        sortable: false,
        center: true,
        minWidth: "30%",
        maxWidth: "30%",

        cell: (row) => (
          <>
            {getSellerById(row.sellerId) === "Unknown"
              ? userData?.role === "seller"
                ? userData?.name
                : "Unknown"
              : getSellerById(row.sellerId)}
          </>
        ),
      },
      {
        name: "Product",
        selector: (row) => getProductById(row.productId),
        sortable: false,
        center: true,
        minWidth: "30%",
        maxWidth: "30%",
      },

      {
        name: "Assignment Date",
        selector: (row) => row.assignmentDate,
        sortable: false,
        center: true,
        minWidth: "25%",
        maxWidth: "25%",
      },
    ],
    []
  );

  const handleSearch = useCallback(
    (e) => {
      const query = e.target.value.trim();
      dispatch(query ? searchTags(query) : clearTagSearch());
    },
    [dispatch]
  );

  const handleSort = useCallback(
    (sortOption) => {
      dispatch(sortTags(sortOption));
    },
    [dispatch]
  );

  const handleManageClick = (action) => {
    switch (action) {
      case "import":
        setIsImportTagsModalOpen(true);
        break;
      case "enable-disable":
        setIsEnableDisableModalOpen(true);
        break;
      case "assign":
        setIsAssignTagsModalOpen(true);
        break;
      default:
        break;
    }
  };

  const manageDropdownItems = isAdmin
    ? ["import", "enable-disable", "assign"]
    : ["assign", "enable-disable"];

  return (
    <Fragment>
      {loading && <CuteMascotSpinner />}
      <ImportTagsModal
        openModal={isImportTagsModalOpen}
        toggle={() => setIsImportTagsModalOpen(false)}
        sellerData={sellerData}
        setLoading={setLoading}
      />
      <AssignTagsModal
        openModal={isAssignTagsModalOpen}
        toggle={() => setIsAssignTagsModalOpen(false)}
        brandsData={brandsData}
        productsData={productsData}
        sellerData={sellerData}
        setLoading={setLoading}
      />
      <EnableDisable
        openModal={isEnableDisableModalOpen}
        toggle={() => setIsEnableDisableModalOpen(!isEnableDisableModalOpen)}
        setLoading={setLoading}
      />
      <TagDetailModal
        openModal={isTagDetailModalOpen}
        toggle={() => setIsTagDetailModalOpen(false)}
        currentTag={currentTag}
        getBrandNameById={getBrandNameById}
        getProductById={getProductById}
        getSellerById={getSellerById}
        getLocationById={getLocationById}
      />

      <div
        className={`${noTags ? styles.full_height : styles.auto_height} ${
          styles.tags_container
        }`}
      >
        <div className={styles.tags_inner_container}>
          <div className={styles._page_header_wrapper}>
            <div className={styles.header_wrapper}>
              <p className={styles.brand_heading}>Tags</p>
              <Row className={styles.brand_header}>
                <Col xs={12} className={styles.filters_div}>
                  <Input
                    className={styles.search_textfield}
                    placeholder="Search"
                    onChange={handleSearch}
                  />
                  <Filterdropdown
                    SortIcon={SortIcon}
                    dropdownItems={
                      <DropdownMenu className={styles.dropdown_menu}>
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            gap: "10px",
                            padding: "10px 0",
                          }}
                        >
                          {[
                            "Brand ID (A-Z)",
                            "Seller ID (A-Z)",
                            "Product ID (A-Z)",
                          ].map((option, index) => (
                            <DropdownItem
                              className={styles.dropdown_item}
                              key={index}
                              onClick={() => handleSort(option)}
                            >
                              {option}
                            </DropdownItem>
                          ))}
                        </div>
                      </DropdownMenu>
                    }
                    toggleIcon={DropdownIcon}
                    text="Sort"
                  />
                  <Dropdown
                    isOpen={isOptionsDropdownOpen}
                    toggle={() =>
                      setIsOptionsDropdownOpen(!isOptionsDropdownOpen)
                    }
                  >
                    <DropdownToggle
                      className={`${styles.enable_disable_button} ${styles.dropdown_toggle_menu}`}
                    >
                      <div>Manage</div>
                      <div>
                        <img
                          src={ArrowDownBlack}
                          alt="arrow"
                          width={20}
                          height={20}
                        />
                      </div>
                    </DropdownToggle>
                    <DropdownMenu className={styles.dropdown_menu_options}>
                      {manageDropdownItems.map((action) => (
                        <DropdownItem
                          key={action}
                          className={styles.dropdown_menu_item}
                          onClick={() => handleManageClick(action)}
                        >
                          {action === "import"
                            ? "Import"
                            : action === "enable-disable"
                            ? "Enable/Disable"
                            : "Assign"}
                        </DropdownItem>
                      ))}
                    </DropdownMenu>
                  </Dropdown>
                  <button className={styles["order_button"]}>Order</button>
                </Col>
              </Row>
            </div>
          </div>

          {noTags ? (
            <div className={styles.no_location_layout}>
              <img src={TagsIcon} alt="no-loc" width={120} height={120} />
              <p className={styles.no_location_header}>Tags</p>
              <p className={styles.no_location_paragraph}>
                You don't have any tags yet.
              </p>

              <Button
                className={styles.enable_disable_button}
                onClick={() => setIsAssignTagsModalOpen(true)}
              >
                Assign
              </Button>
            </div>
          ) : (
            <div className={styles.table_container}>
              <DataTableComponent
                tableColumns={tagsColumns}
                tableData={tagsTableData}
                highlightOnHover
                tableCustomStyles={{
                  cells: {
                    style: {
                      justifyContent: "flex-start",
                      display: "flex",
                    },
                  },
                }}
                onRowClicked={(row) => {
                  setCurrentTag(row);
                  setIsTagDetailModalOpen(true);
                }}
              />
            </div>
          )}
        </div>
      </div>
    </Fragment>
  );
};

export default TagsList;
